import { useState, useEffect, useRef, type ChangeEvent, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  Download,
  ExternalLink,
  Eye,
  FileText,
  Loader2,
  Sparkles,
  Upload,
  X,
  Edit,
  Save,
} from 'lucide-react';
import { ApiError, call, downloadUrl, getToken } from '@/lib/apiClient';
import { useStandardize, useUploadDraft, standardizeApi } from '@/queries/standardize.queries';
import type { DocFormat, Severity, StandardizeResult } from '@/lib/types';
import { Badge, Button, Field, Input, Textarea, cx } from '@/components/ui';

const SEVERITY_ORDER: Record<Severity, number> = { BLOCK: 0, WARN: 1, NOTE: 2 };
const SEV_TONE: Record<Severity, 'danger' | 'warn' | 'muted'> = {
  BLOCK: 'danger',
  WARN: 'warn',
  NOTE: 'muted',
};
const STAGE_TONE: Record<string, string> = {
  ok: 'text-success',
  skipped: 'text-muted-foreground',
  blocked: 'text-destructive',
  error: 'text-destructive',
};

export function StandardizerView() {
  const [title, setTitle] = useState('');
  const [format, setFormat] = useState<DocFormat>('docx');
  const [content, setContent] = useState('');

  // Original-file state (only set for editable .docx/.pptx uploads).
  const [sourceFormat, setSourceFormat] = useState<DocFormat | null>(null);
  const [originalFileBase64, setOriginalFileBase64] = useState<string | null>(null);
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  const [emptySections, setEmptySections] = useState<any[]>([]);

  const [activeUploadId, setActiveUploadId] = useState<string | null>(null);
  const [adeStatus, setAdeStatus] = useState<'processing' | 'done' | 'failed' | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [result, setResult] = useState<StandardizeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [promptInstructions, setPromptInstructions] = useState('');
  const [sessionPreviewData, setSessionPreviewData] = useState<any>(null);
  const [isSessionDirty, setIsSessionDirty] = useState(false);

  const uploadMutation = useUploadDraft();
  const standardizeMutation = useStandardize();
  const busy = standardizeMutation.isPending;
  const uploading = uploadMutation.isPending;

  function clearOriginal() {
    setSourceFormat(null);
    setOriginalFileBase64(null);
    setUploadedName(null);
    setEmptySections([]);
    setActiveUploadId(null);
    setAdeStatus(null);
  }

  async function handleFile(file: File) {
    setError(null);
    setActiveUploadId(null);
    setAdeStatus(null);
    try {
      const up = await uploadMutation.mutateAsync(file);
      setContent(up.text);
      setSourceFormat(up.sourceFormat);
      setOriginalFileBase64(up.originalFileBase64);
      setUploadedName(up.filename);
      setEmptySections(up.emptySections || []);
      setActiveUploadId(up.uploadId || null);
      setAdeStatus(up.adeStatus || null);
      if (!title.trim()) setTitle(up.filename.replace(/\.[^.]+$/, ''));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Upload/convert failed');
    }
  }

  useEffect(() => {
    if (adeStatus !== 'processing' || !activeUploadId) return;

    let timerId: number;
    let isActive = true;

    async function poll() {
      if (!isActive) return;
      try {
        const res = await standardizeApi.getAdeFallbackStatus(activeUploadId!);
        
        if (!isActive) return;

        if (res.status === 'done') {
          setAdeStatus('done');
          if (res.markdown) {
            setContent(res.markdown);
          }
          setEmptySections([]); // Clear unreadable warnings on success
        } else if (res.status === 'failed') {
          setAdeStatus('failed');
        } else {
          timerId = window.setTimeout(poll, 2500);
        }
      } catch (err) {
        console.error('[ADE Fallback] Polling error:', err);
        setAdeStatus('failed');
      }
    }

    timerId = window.setTimeout(poll, 2500);

    return () => {
      isActive = false;
      clearTimeout(timerId);
    };
  }, [adeStatus, activeUploadId]);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleFile(file);
    e.target.value = '';
  }

  function onDragOver(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    if (uploading) return;
    setIsDragging(true);
  }

  function onDragLeave(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setIsDragging(false);
  }

  async function onDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setIsDragging(false);
    if (uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowed = ['docx', 'pptx', 'pdf', 'txt', 'md'];
    if (!ext || !allowed.includes(ext)) {
      setError('Unsupported file type. Allowed formats: .docx, .pptx, .pdf, .txt, .md');
      return;
    }

    await handleFile(file);
  }

  // If the user hand-edits the draft, drop the stale original so we regenerate
  // a fresh file instead of silently editing the uploaded one in place.
  function onContentChange(e: ChangeEvent<HTMLTextAreaElement>) {
    setContent(e.target.value);
    if (originalFileBase64) clearOriginal();
  }

  async function run(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    setSessionPreviewData(null);
    setIsSessionDirty(false);
    try {
      setResult(
        await standardizeMutation.mutateAsync({
          title: title.trim() || 'Standardized Document',
          author: '',
          version: '1.0',
          department: '',
          purpose: '',
          format,
          content,
          sourceFormat,
          originalFileBase64,
          promptInstructions,
        }),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Standardizer run failed');
    }
  }

  const changes = result
    ? [...result.changes].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity])
    : [];

  // Upload status hint.
  let uploadHint: string | null = null;
  if (uploadedName && sourceFormat) {
    uploadHint =
      sourceFormat === format
        ? `${sourceFormat === 'docx' ? 'Word' : 'PowerPoint'} file will be edited in place (design preserved)`
        : 'Output format differs — a new file will be generated';
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <form onSubmit={run} className="bg-card border border-border rounded p-6 flex flex-col gap-5">
        <Field label="Title">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={busy}
            placeholder="Document title"
          />
        </Field>

        {/* Upload control */}
        <div className="flex flex-col gap-1.5">
          <label
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={cx(
              'flex items-center gap-3 p-4 rounded-md border border-dashed transition-colors cursor-pointer text-sm text-muted-foreground',
              isDragging
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary/40',
              (busy || uploading) && 'opacity-50 cursor-not-allowed pointer-events-none',
            )}
          >
            <span className="grid place-items-center w-9 h-9 rounded-md bg-primary text-primary-foreground shrink-0">
              {uploading ? (
                <Loader2 className="w-[17px] h-[17px] animate-spin" />
              ) : (
                <Upload className="w-[17px] h-[17px]" />
              )}
            </span>
            <span>
              <span className="text-foreground">Upload a file</span> — Word, PowerPoint, PDF, text,
              or markdown. Or drag and drop here, or paste below.
            </span>
            <input
              type="file"
              accept=".docx,.pptx,.pdf,.txt,.md"
              onChange={onFile}
              disabled={uploading || busy}
              className="hidden"
            />
            {uploading && <span className="ml-auto text-muted-foreground text-xs">Converting…</span>}
          </label>
          {uploadHint && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="text-foreground font-mono">{uploadedName}</span>
              <span>· {uploadHint}</span>
            </p>
          )}
          {adeStatus === 'processing' && (
            <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded text-primary text-xs mt-2 font-medium">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              <div>
                Reading {emptySections.length} page{emptySections.length > 1 ? 's' : ''} with AI… (Running in background)
              </div>
            </div>
          )}
          {adeStatus === 'done' && (
            <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded text-emerald-600 dark:text-emerald-400 text-xs mt-2 font-medium">
              <Check className="w-4 h-4 shrink-0" />
              <div>
                AI transcription and page insertion complete.
              </div>
            </div>
          )}
          {adeStatus !== 'processing' && emptySections.length > 0 && (
            <div className="flex gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded text-amber-600 dark:text-amber-400 text-xs mt-2 font-medium">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <div>
                Some pages could not be read (likely scanned or image-based) and were left blank in the draft. You may need to add that content manually.
              </div>
            </div>
          )}
        </div>

        <div className={cx(
          'transition-all duration-300',
          format === 'html' ? 'opacity-0 hover:opacity-100 h-12 hover:h-64 overflow-hidden border border-dashed border-border rounded-md p-4 bg-muted/20' : ''
        )}>
          <Field label={format === 'html' ? "Draft text (Hover to reveal source text)" : "Draft text"}>
            <Textarea
              value={content}
              onChange={onContentChange}
              disabled={busy}
              required={format !== 'html'}
              rows={format === 'html' ? 6 : 8}
              placeholder="Paste the draft to standardize…"
            />
          </Field>
        </div>

        {format === 'html' && (
          <Field label="Prompt / Page Instructions">
            <Textarea
              value={promptInstructions}
              onChange={(e) => setPromptInstructions(e.target.value)}
              disabled={busy}
              placeholder="Describe the HTML page structure (e.g. 'Create a clean, responsive landing page for the Security team with cards and dark branding')..."
              rows={4}
            />
          </Field>
        )}

        {/* Output format toggle (below the draft) */}
        <Field label="Output format">
          <div className="inline-flex flex-wrap p-1 rounded-md bg-muted border border-border gap-1">
            {(['docx', 'pptx', 'pdf', 'html', 'md'] as const).map((f) => (
              <button
                key={f}
                type="button"
                disabled={busy}
                onClick={() => setFormat(f)}
                className={cx(
                  'h-8 px-4 rounded text-sm font-medium transition-all',
                  format === f
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                  busy && 'opacity-50 cursor-not-allowed',
                )}
              >
                {f === 'docx' && 'Word (docx)'}
                {f === 'pptx' && 'PowerPoint (pptx)'}
                {f === 'pdf' && 'PDF Document'}
                {f === 'html' && 'HTML Page'}
                {f === 'md' && 'Markdown (md)'}
              </button>
            ))}
          </div>
        </Field>

        <div className="flex flex-col gap-4 border-t border-border pt-4">
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={busy || (format !== 'html' && !content.trim()) || (format === 'html' && !content.trim() && !promptInstructions.trim())}>
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Standardizing…
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Standardize
                </>
              )}
            </Button>
          </div>
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </form>

      {result && (
        <div className="bg-card border border-border rounded p-6 flex flex-col gap-5 animate-rise">
          <div className="flex flex-wrap items-center gap-3">
            <Badge tone={result.verdict === 'pass' ? 'ok' : 'danger'}>
              {result.verdict.toUpperCase()}
            </Badge>
            <span className="font-mono text-sm text-foreground">{result.docId}</span>
            {result.traceUrl && (
              <a
                href={result.traceUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                View trace <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <span className="ml-auto text-xs text-muted-foreground font-mono">
              {result.summary.block} block · {result.summary.warn} warn · {result.summary.note} note
            </span>
          </div>

          <div className="flex items-center justify-between p-4 rounded-md border border-border bg-card">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-md bg-primary/10 text-primary">
                <FileText className="w-[18px] h-[18px]" />
              </span>
              <div>
                <span className="block text-foreground font-medium">
                  {result.format === 'html' ? 'Generated HTML Page' : result.download.filename}
                </span>
                <span className="text-xs text-muted-foreground">
                  {Math.round(result.download.byteSize / 1024)} KB
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="subtle"
                size="sm"
                onClick={() => setIsPreviewOpen(true)}
                title="Preview file"
                className="h-9 w-9 p-0"
              >
                <Eye className="w-4 h-4" />
              </Button>
              <a
                href={downloadUrl(`/standardize/download/${result.download.id}`)}
                download
                title="Download file"
                className="inline-flex items-center justify-center rounded-md border border-border hover:border-primary hover:bg-secondary-foreground/10 h-9 w-9 p-0 bg-secondary text-secondary-foreground transition-all"
              >
                <Download className="w-4 h-4" />
              </a>
            </div>
          </div>

          {result.unappliedCount > 0 && (
            <p className="flex items-center gap-2 text-sm text-warning">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {result.unappliedCount} change(s) couldn&apos;t be auto-located in the original file —
              review manually.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {result.stages.map((s) => (
              <span
                key={s.stage}
                title={s.detail}
                className="inline-flex items-center gap-1.5 px-3 h-7 rounded-md bg-muted border border-border text-xs"
              >
                <span className="text-foreground">{s.stage}</span>
                <span className={cx('font-mono', STAGE_TONE[s.status] ?? 'text-muted-foreground')}>
                  {s.status}
                </span>
              </span>
            ))}
          </div>

          {changes.length > 0 && (
            <div className="flex flex-col gap-2.5">
              <h3 className="text-sm font-semibold text-foreground">Changes</h3>
              {changes.map((c) => (
                <div key={c.id} className="rounded-md bg-muted border border-border p-3.5">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <Badge tone={SEV_TONE[c.severity]}>{c.severity}</Badge>
                    <span className="font-mono text-xs text-muted-foreground">
                      {c.id} · {c.category}
                    </span>
                    {!c.fixed && (
                      <span className="inline-flex items-center gap-1 text-xs text-warning">
                        <AlertTriangle className="w-3 h-3" />
                        needs review
                      </span>
                    )}
                    {c.fixed && (
                      <span className="inline-flex items-center gap-1 text-xs text-success">
                        <Check className="w-3 h-3" />
                        fixed
                      </span>
                    )}
                  </div>
                  {(c.before || c.after) && (
                    <p className="text-sm">
                      {c.before && (
                        <span className="line-through text-destructive/80">{c.before}</span>
                      )}{' '}
                      {c.after && <span className="text-success">{c.after}</span>}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-muted-foreground">{c.reason}</p>
                </div>
              ))}
            </div>
          )}

          {result.format !== 'html' && (
            <details className="rounded-md bg-muted border border-border p-3.5">
              <summary className="cursor-pointer text-sm font-medium text-foreground">
                Corrected text
              </summary>
              <pre className="mt-3 text-xs text-muted-foreground whitespace-pre-wrap font-mono leading-relaxed">
                {result.correctedText}
              </pre>
            </details>
          )}
        </div>
      )}

      {isPreviewOpen && result && (
        <PreviewModal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          fileId={result.download.id}
          filename={result.download.filename}
          format={result.format}
          previewData={sessionPreviewData}
          setPreviewData={setSessionPreviewData}
          isSessionDirty={isSessionDirty}
          setIsSessionDirty={setIsSessionDirty}
        />
      )}
    </div>
  );
}

function markdownToHtml(md: string): string {
  if (!md) return '';
  let html = md;

  // Convert markdown headings
  html = html.replace(/^#### (.*?)$/gm, '<h4>$1</h4>');
  html = html.replace(/^### (.*?)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.*?)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.*?)$/gm, '<h1>$1</h1>');

  // Convert bold/italic
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');

  // Auto-structure plain extracted text (e.g. from PDFs) into branded headings & sections if no heading tags present
  if (!/<h[1-6]/i.test(html)) {
    const rawLines = html.split('\n');
    let isFirstNonEmpty = true;
    const structuredLines = rawLines.map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return '';
      // Strip any page number / header / footer artifact lines like "Page 1", "---", "page1---", "Saint Fox · Confidential Page 1 of 3"
      if (/^(page\s*\d+|\-\-\-+|\d+\s*\/\s*\d+|page\s*\d+\s*\-\-\-|saint fox\s*·?\s*(confidential|document)?\s*(page\s*\d+)?)/i.test(trimmed)) {
        return '';
      }
      if (isFirstNonEmpty && trimmed.length > 0 && trimmed.length < 80) {
        isFirstNonEmpty = false;
        return `<h1>${trimmed}</h1>`;
      }
      if (/^(executive summary|table of contents|introduction|overview|background|verdict|change log|key findings|recommendations|conclusion|signatures?|metadata|document details)/i.test(trimmed)) {
        return `<h2>${trimmed}</h2>`;
      }
      if (/^[A-Z0-9\s\-\:\.]{4,60}$/.test(trimmed) && !trimmed.endsWith('.') && !trimmed.includes('<')) {
        return `<h3>${trimmed}</h3>`;
      }
      return line;
    });
    html = structuredLines.join('\n');
  }

  // Convert tables
  html = html.replace(/\|(.*?)\|\r?\n\|[ -:|]*\|\r?\n((?:\|.*?\|\r?\n?)*)/g, (_match, headerLine, bodyLines) => {
    const headers = headerLine.split('|').map((h: string) => h.trim()).filter(Boolean);
    const rows = bodyLines.trim().split('\n').map((line: string) => 
      line.split('|').map((cell: string) => cell.trim()).filter(Boolean)
    ).filter((r: any) => r.length > 0);
    
    let tableHtml = '<table style="width: 100%; border-collapse: collapse; margin: 16px 0; border: 1px solid var(--color-border);">';
    tableHtml += '<thead><tr style="background-color: var(--color-muted);">';
    headers.forEach((h: string) => {
      tableHtml += `<th style="border: 1px solid var(--color-border); padding: 8px; text-align: left; font-weight: 600;">${h}</th>`;
    });
    tableHtml += '</tr></thead><tbody>';
    rows.forEach((row: any) => {
      tableHtml += '<tr>';
      row.forEach((cell: string) => {
        tableHtml += `<td style="border: 1px solid var(--color-border); padding: 8px;">${cell}</td>`;
      });
      tableHtml += '</tr>';
    });
    tableHtml += '</tbody></table>';
    return tableHtml;
  });

  // Convert bullet lists (simple)
  html = html.replace(/^\s*-\s+(.*?)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*?<\/li>)+/g, '<ul>$&</ul>');

  // Convert paragraphs (lines that don't start with tags)
  const lines = html.split('\n');
  const processedLines = lines.map(line => {
    const trimmed = line.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('<h') || trimmed.startsWith('<ul') || trimmed.startsWith('<ol') || trimmed.startsWith('<li') || trimmed.startsWith('<table') || trimmed.startsWith('<tr') || trimmed.startsWith('<td') || trimmed.startsWith('<th') || trimmed.startsWith('<thead') || trimmed.startsWith('<tbody') || trimmed.startsWith('<div')) {
      return line;
    }
    return `<p>${line}</p>`;
  });
  
  return processedLines.filter(Boolean).join('\n');
}


const BRANDED_CLASSES = "standard-document-preview";

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileId: string;
  filename: string;
  format: string;
  previewData?: any;
  setPreviewData?: React.Dispatch<React.SetStateAction<any>>;
  isSessionDirty?: boolean;
  setIsSessionDirty?: (dirty: boolean) => void;
  canEdit?: boolean;
}

export function PreviewModal({ 
  isOpen, 
  onClose, 
  fileId, 
  filename, 
  format,
  previewData,
  setPreviewData,
  isSessionDirty,
  setIsSessionDirty,
  canEdit = true,
}: PreviewModalProps) {
  const [loading, setLoading] = useState(false);
  const [localPreviewData, setLocalPreviewData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedHtml, setEditedHtml] = useState('');
  const [editedMarkdown, setEditedMarkdown] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeBox, setActiveBox] = useState<{ slideIdx: number; boxIdx: number } | null>(null);

  const [isDirty, setIsDirty] = useState(false);
  const [localIsSessionDirty, setLocalIsSessionDirty] = useState(false);
  const [hasSessionEdits, setHasSessionEdits] = useState(false);
  const [pdfKey, setPdfKey] = useState(0);
  const [selectedFont, setSelectedFont] = useState('Inter');

  // Map properties to use either shared or local state:
  const data = previewData !== undefined ? previewData : localPreviewData;
  const setData = setPreviewData !== undefined ? setPreviewData : setLocalPreviewData;
  const isSessionDirtyState = isSessionDirty !== undefined ? isSessionDirty : localIsSessionDirty;
  const setIsSessionDirtyState = setIsSessionDirty !== undefined ? setIsSessionDirty : setLocalIsSessionDirty;

  const editorRef = useRef<HTMLDivElement>(null);
  const savedBaselineRef = useRef<any>(null);
  const hasInitializedEditorRef = useRef(false);

  const prevFileIdRef = useRef(fileId);
  useEffect(() => {
    if (prevFileIdRef.current !== fileId) {
      setEditedHtml('');
      setEditedMarkdown('');
      setIsDirty(false);
      setHasSessionEdits(false);
      setData(null);
      setError(null);
      setIsEditing(false);
      savedBaselineRef.current = null;
      hasInitializedEditorRef.current = false;
      prevFileIdRef.current = fileId;
    }
  }, [fileId]);

  useEffect(() => {
    if (!isOpen) return;
    
    // Only fetch if data is null, OR if format is PDF/HTML and we toggled edit mode on but don't have json yet
    const needsFetch = !data || (['pdf', 'html'].includes(format) && isEditing && !data.html && data.type !== 'pdf' && data.type !== 'html');
    if (!needsFetch) return;

    if (!isEditing && ['pdf', 'html'].includes(format)) {
      return;
    }

    async function fetchPreview() {
      setLoading(true);
      setError(null);
      try {
        const url = `/standardize/preview/${fileId}?editor=true`;
        const res = await call<any>(url);
        
        if (res && (res.type === 'md' || res.type === 'pdf')) {
          const htmlContent = markdownToHtml(res.content);
          const fullRes = { ...res, html: htmlContent };
          setData(fullRes);
          savedBaselineRef.current = JSON.parse(JSON.stringify(fullRes));
        } else {
          setData(res);
          savedBaselineRef.current = JSON.parse(JSON.stringify(res));
        }
      } catch (err: any) {
        setError(err instanceof Error ? err.message : 'Could not fetch document preview');
      } finally {
        setLoading(false);
      }
    }

    fetchPreview();
  }, [isOpen, fileId, format, isEditing, data]);

  useEffect(() => {
    if (data) {
      if (['docx', 'html', 'md', 'pdf'].includes(format)) {
        setEditedHtml(data.html || '');
      }
      if (format === 'md') {
        setEditedMarkdown(data.content || '');
      }
      if (!savedBaselineRef.current) {
        savedBaselineRef.current = JSON.parse(JSON.stringify(data));
      }
      setIsDirty(false);
    }
  }, [data, format]);

  const revertEdits = () => {
    hasInitializedEditorRef.current = false;
    if (savedBaselineRef.current) {
      const cleanData = JSON.parse(JSON.stringify(savedBaselineRef.current));
      setData(cleanData);
      if (cleanData.html) {
        setEditedHtml(cleanData.html);
        if (editorRef.current) {
          editorRef.current.innerHTML = cleanData.html;
        }
      }
      if (format === 'md') {
        setEditedMarkdown(cleanData.content || '');
      }
    } else {
      if (data?.html) {
        setEditedHtml(data.html);
        if (editorRef.current) {
          editorRef.current.innerHTML = data.html;
        }
      }
      if (format === 'md') {
        setEditedMarkdown(data?.content || '');
      }
    }
    setIsDirty(false);
    setIsSessionDirtyState(false);
    setHasSessionEdits(false);
  };

  // Initialize contentEditable DOM once when entering edit mode (prevents cursor jump)
  useEffect(() => {
    if (isEditing && editorRef.current) {
      if (!hasInitializedEditorRef.current && editedHtml) {
        editorRef.current.innerHTML = editedHtml;
        hasInitializedEditorRef.current = true;
      }
    } else if (!isEditing) {
      hasInitializedEditorRef.current = false;
    }
  }, [isEditing, editedHtml]);

  if (!isOpen) return null;

  const handleSelectBox = (slideIdx: number, boxIdx: number) => {
    setActiveBox({ slideIdx, boxIdx });
  };

  const handleMarkDirty = () => {
    setIsDirty(true);
    setIsSessionDirtyState(true);
  };

  const handleSlideTextChange = (slideIdx: number, boxIdx: number, newText: string) => {
    setIsDirty(true);
    setIsSessionDirtyState(true);
    setData((prev: any) => {
      if (!prev || !prev.slides) return prev;
      const updatedSlides = prev.slides.map((slide: any, sIdx: number) => {
        if (sIdx !== slideIdx) return slide;
        const updatedBoxes = slide.textBoxes.map((box: any, bIdx: number) => {
          if (bIdx !== boxIdx) return box;
          return { ...box, text: newText };
        });
        return { ...slide, textBoxes: updatedBoxes };
      });
      return { ...prev, slides: updatedSlides };
    });
  };

  const handleDeleteSlide = (slideIdx: number) => {
    setIsDirty(true);
    setIsSessionDirtyState(true);
    setData((prev: any) => {
      if (!prev || !prev.slides) return prev;
      const updatedSlides = prev.slides.filter((_: any, sIdx: number) => sIdx !== slideIdx);
      return { ...prev, slides: updatedSlides };
    });
  };

  const handleContentInput = (e: React.FormEvent<HTMLDivElement>) => {
    const html = e.currentTarget.innerHTML;
    setEditedHtml(html);
    setIsDirty(true);
    setIsSessionDirtyState(true);
  };

  // Modal Popups & Toast state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'discard_close' | 'discard_toggle' | 'download_unsaved';
    title: string;
    message: string;
  } | null>(null);

  const [toast, setToast] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const showToast = (text: string, type: 'success' | 'error' = 'success') => {
    setToast({ text, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  const handleToggleEditMode = () => {
    if (isEditing) {
      if (isDirty || isSessionDirtyState) {
        setConfirmModal({
          isOpen: true,
          type: 'discard_toggle',
          title: 'Unsaved Changes',
          message: 'You have unsaved changes. Do you want to proceed and discard your edits?',
        });
        return;
      }
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  };

  const handleClose = () => {
    if (isDirty || isSessionDirtyState) {
      setConfirmModal({
        isOpen: true,
        type: 'discard_close',
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Do you want to proceed and discard your edits?',
      });
      return;
    }
    onClose();
  };

  const handleSaveChangesToDb = async (e?: React.MouseEvent): Promise<boolean> => {
    if (e) e.preventDefault();
    if (!data) return false;

    setSaving(true);
    try {
      const token = getToken();
      const headers = new Headers();
      headers.set('Content-Type', 'application/json');
      if (token) headers.set('Authorization', `Bearer ${token}`);

      const payload: any = {
        title: filename.replace(/\.[^.]+$/, ''),
        format,
        fontFamily: selectedFont,
      };

      if (format === 'pptx') {
        payload.slides = data.slides;
      } else if (format === 'md') {
        payload.content = editedMarkdown;
      } else {
        let contentToSave = editedHtml;
        if (isEditing && editorRef.current) {
          contentToSave = editorRef.current.innerHTML;
        } else if (data.html) {
          contentToSave = data.html;
        }
        payload.content = contentToSave;
      }

      const response = await fetch(`/api/standardize/update/${fileId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to save changes to database');
      }

      setIsDirty(false);
      setIsSessionDirtyState(false);
      setHasSessionEdits(true);
      setIsEditing(false);
      setPdfKey((prev) => prev + 1);

      // Re-fetch updated preview from database to guarantee 100% sync
      try {
        const updatedRes = await call<any>(`/standardize/preview/${fileId}?editor=true`);
        if (updatedRes && (updatedRes.type === 'md' || updatedRes.type === 'pdf')) {
          const htmlContent = markdownToHtml(updatedRes.content);
          const fullRes = { ...updatedRes, html: htmlContent };
          setData(fullRes);
          savedBaselineRef.current = JSON.parse(JSON.stringify(fullRes));
          if (fullRes.html) setEditedHtml(fullRes.html);
          if (format === 'md' && fullRes.content) setEditedMarkdown(fullRes.content);
        } else if (updatedRes) {
          setData(updatedRes);
          savedBaselineRef.current = JSON.parse(JSON.stringify(updatedRes));
          if (updatedRes.html) setEditedHtml(updatedRes.html);
          if (format === 'md' && updatedRes.content) setEditedMarkdown(updatedRes.content);
        }
      } catch (_ignore) {
        savedBaselineRef.current = JSON.parse(JSON.stringify(data));
      }

      showToast('Changes saved successfully to database.', 'success');
      return true;
    } catch (err: any) {
      showToast('Save failed: ' + err.message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (e?: React.MouseEvent, bypassUnsavedCheck: boolean = false) => {
    if (e) e.preventDefault();

    if ((isDirty || isSessionDirtyState) && !bypassUnsavedCheck) {
      setConfirmModal({
        isOpen: true,
        type: 'download_unsaved',
        title: 'Unsaved Changes',
        message: 'You have unsaved changes in your document. Would you like to save them before downloading?',
      });
      return;
    }

    if (!isDirty && !hasSessionEdits && !isSessionDirtyState) {
      window.location.href = downloadUrl(`/standardize/download/${fileId}`);
      return;
    }

    setSaving(true);
    try {
      const token = getToken();
      const headers = new Headers();
      headers.set('Content-Type', 'application/json');
      if (token) headers.set('Authorization', `Bearer ${token}`);

      const payload: any = {
        title: filename.replace(/\.[^.]+$/, ''),
        format,
        fontFamily: selectedFont,
      };

      if (format === 'pptx') {
        payload.slides = data.slides;
      } else if (format === 'md') {
        payload.content = editedMarkdown;
      } else {
        let contentToSave = editedHtml;
        if (isEditing && editorRef.current) {
          contentToSave = editorRef.current.innerHTML;
        } else if (data.html) {
          contentToSave = data.html;
        }
        payload.content = contentToSave;
      }

      const response = await fetch('/api/standardize/export', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to generate edited document');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition');
      const filenameMatch = disposition && disposition.match(/filename="(.+?)"/);
      a.download = filenameMatch ? filenameMatch[1] : `${filename.replace(/\.[^.]+$/, '')}_edited.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      showToast('Export failed: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmAction = async (action: 'confirm' | 'cancel' | 'download_without_save') => {
    if (!confirmModal) return;
    const currentType = confirmModal.type;
    setConfirmModal(null);

    if (currentType === 'discard_close') {
      if (action === 'confirm') {
        revertEdits();
        onClose();
      }
    } else if (currentType === 'discard_toggle') {
      if (action === 'confirm') {
        revertEdits();
        setIsEditing(false);
      }
    } else if (currentType === 'download_unsaved') {
      if (action === 'confirm') {
        const saved = await handleSaveChangesToDb();
        if (saved) {
          window.location.href = downloadUrl(`/standardize/download/${fileId}`);
        }
      } else if (action === 'download_without_save') {
        handleDownload(undefined, true);
      }
    }
  };



  const renderSlide = (slideIndex: number, isEditMode: boolean) => {
    const slide = data?.slides?.[slideIndex];
    if (!slide) return null;

    const isCover = slideIndex === 0;
    const isSection = slide.textBoxes?.some((box: any) => box.text?.trim()?.toUpperCase() === "SECTION DIVIDER");

    let slideBgClass = "bg-[#F8FAFC]";
    if (isCover) slideBgClass = "bg-[#0F172A]";
    else if (isSection) slideBgClass = "bg-[#1E293B]";

    return (
      <div className={cx("relative w-full aspect-[16/9] border border-border shadow-md rounded-md overflow-hidden select-none pptx-slide-container", slideBgClass)}>
        {/* Render Cover Slide shapes */}
        {isCover && (
          <>
            {/* Right side teal shape */}
            <div 
              style={{
                position: 'absolute',
                left: '70.0%',
                top: 0,
                width: '30.0%',
                height: '100%',
                backgroundColor: '#15B3A4'
              }}
            />
            {/* Dark triangle jutting in */}
            <svg 
              viewBox="0 0 100 100" 
              preserveAspectRatio="none" 
              style={{
                position: 'absolute',
                left: '62.5%',
                top: '33.3%',
                width: '15.0%',
                height: '33.3%',
                fill: '#0F172A',
                border: 'none'
              }}
            >
              <polygon points="0,0 100,50 0,100" />
            </svg>
          </>
        )}

        {/* Render Left Brand Sidebar (for non-cover slides) */}
        {!isCover && (
          <div 
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: isSection ? '1.88%' : '0.9%',
              height: '100%',
              backgroundColor: '#15B3A4'
            }}
          />
        )}

        {/* Render Content Card (for standard slides only) */}
        {!isCover && !isSection && (
          <div 
            style={{
              position: 'absolute',
              left: '4.5%',
              top: '17.3%',
              width: '90.8%',
              height: '72.0%',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '8px'
            }}
          />
        )}

        {/* Render Text Boxes */}
        {slide.textBoxes?.map((box: any, boxIdx: number) => {
          const isActive = isEditMode && activeBox?.slideIdx === slideIndex && activeBox?.boxIdx === boxIdx;
          return (
            <div
              key={boxIdx}
              contentEditable={isEditMode}
              suppressContentEditableWarning
              onClick={isEditMode ? () => handleSelectBox(slideIndex, boxIdx) : undefined}
              onInput={isEditMode ? handleMarkDirty : undefined}
              onKeyUp={isEditMode ? handleMarkDirty : undefined}
              onBlur={isEditMode ? (e) => handleSlideTextChange(slideIndex, boxIdx, e.currentTarget.innerText) : undefined}
              style={{
                position: 'absolute',
                left: `${box.x * 100}%`,
                top: `${box.y * 100}%`,
                width: `${box.w * 100}%`,
                height: `${box.h * 100}%`,
                fontFamily: selectedFont || box.fontFace || 'Calibri',
                fontSize: `${box.fontSize ? (box.fontSize / 9.6) : (14 / 9.6)}cqw`,
                color: `#${box.fontColor || (isCover || isSection ? 'FFFFFF' : '1E293B')}`,
                fontWeight: box.bold ? 'bold' : 'normal',
                fontStyle: box.italic ? 'italic' : 'normal',
                textDecoration: box.underline ? 'underline' : 'none',
                textAlign: box.align as any,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                outline: isActive ? '1px dashed #15B3A4' : (isEditMode ? '1px dashed transparent' : 'none'),
                padding: '2px',
                cursor: isEditMode ? 'text' : 'default',
              }}
              className={cx(
                "overflow-y-auto leading-normal",
                isEditMode && "hover:outline-muted-foreground/30"
              )}
            >
              {box.text}
            </div>
          );
        })}

        {/* Footer brand info */}
        <div 
          style={{ color: isCover || isSection ? '#94A3B8' : '#94A3B8' }}
          className="absolute left-[3%] bottom-[3%] text-[2.5%] font-mono select-none pointer-events-none"
        >
          Saint Fox · Confidential
        </div>
        <div 
          style={{ color: isCover || isSection ? '#94A3B8' : '#94A3B8' }}
          className="absolute right-[3%] bottom-[3%] text-[2.5%] font-mono select-none pointer-events-none"
        >
          {slideIndex + 1}
        </div>
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden animate-rise">
        <style dangerouslySetInnerHTML={{ __html: `
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,600&display=swap');

          .standard-document-preview {
            font-family: '${selectedFont}', sans-serif !important;
            color: #222222 !important;
            background-color: #ffffff !important;
            padding: 72px 64px !important;
            margin-bottom: 2rem !important;
            box-sizing: border-box !important;
            min-height: 1056px !important;
          }

          /* Force selected font on ALL child elements, overriding any inline font-family from converted HTML */
          .standard-document-preview * {
            font-family: '${selectedFont}', sans-serif !important;
          }
          
          /* Title styling rules (first paragraph or header inside container) */
          .standard-document-preview > h1:first-child,
          .standard-document-preview > h1:first-child strong,
          .standard-document-preview > p:first-child,
          .standard-document-preview > p:first-child strong,
          .standard-document-preview > p:first-child span {
            font-size: 2.25rem !important;
            font-family: '${selectedFont}', sans-serif !important;
            color: #15b3a4 !important;
            font-weight: 700 !important;
            line-height: 1.2 !important;
            margin-top: 0 !important;
            margin-bottom: 0.5rem !important;
            display: block !important;
          }
          
          /* Subtitle styling rules (second paragraph inside container) */
          .standard-document-preview > p:nth-child(2),
          .standard-document-preview > p:nth-child(2) em,
          .standard-document-preview > p:nth-child(2) span {
            font-style: italic !important;
            color: #666666 !important;
            font-size: 1rem !important;
            margin-bottom: 2rem !important;
            font-weight: normal !important;
            display: block !important;
          }
          
          /* Headers hierarchy styling */
          .standard-document-preview h1,
          .standard-document-preview h1 strong,
          .standard-document-preview h2,
          .standard-document-preview h2 strong,
          .standard-document-preview h3,
          .standard-document-preview h3 strong,
          .standard-document-preview h4,
          .standard-document-preview h4 strong,
          .standard-document-preview h5,
          .standard-document-preview h5 strong {
            font-family: '${selectedFont}', sans-serif !important;
            color: #15b3a4 !important;
            font-weight: 700 !important;
            margin-top: 2rem !important;
            margin-bottom: 1rem !important;
          }
          
          .standard-document-preview h1,
          .standard-document-preview h1 strong {
            font-size: 1.5rem !important;
            margin-top: 2rem !important;
            margin-bottom: 1.25rem !important;
          }
          .standard-document-preview h2,
          .standard-document-preview h2 strong {
            font-size: 1.25rem !important;
            margin-top: 1.75rem !important;
            margin-bottom: 1rem !important;
          }
          .standard-document-preview h3,
          .standard-document-preview h3 strong {
            font-size: 1.1rem !important;
            margin-top: 1.5rem !important;
            margin-bottom: 0.75rem !important;
          }
          
          .standard-document-preview p {
            line-height: 1.5 !important;
            margin-top: 0 !important;
            margin-bottom: 1.25rem !important;
            color: #222222 !important;
          }
          .standard-document-preview ul,
          .standard-document-preview ol {
            margin-top: 0 !important;
            margin-bottom: 1.25rem !important;
            padding-left: 1.5rem !important;
          }
          .standard-document-preview li {
            line-height: 1.5 !important;
            margin-bottom: 0.5rem !important;
            color: #222222 !important;
          }
          .standard-document-preview .metadata {
            font-style: italic !important;
            color: #666666 !important;
            margin-bottom: 2rem !important;
          }
          
          /* Table styling */
          .standard-document-preview table {
            width: 100% !important;
            border-collapse: collapse !important;
            margin-bottom: 1.5rem !important;
            border: 1px solid #000000 !important;
          }
          
          /* Style first row of tables as header row (teal background, bold white text) */
          .standard-document-preview table tr:first-child {
            background-color: #15b3a4 !important;
            color: #ffffff !important;
          }
          .standard-document-preview table tr:first-child td,
          .standard-document-preview table tr:first-child th {
            background-color: #15b3a4 !important;
            color: #ffffff !important;
            font-weight: 700 !important;
            font-family: '${selectedFont}', sans-serif !important;
            font-size: 0.95rem !important;
            border: 1px solid #000000 !important;
          }
          
          .standard-document-preview tr {
            border-bottom: 1px solid #000000 !important;
          }
          .standard-document-preview tr:nth-child(even) td {
            background-color: #f1f5f9 !important;
          }
          .standard-document-preview tr:nth-child(odd) td {
            background-color: #ffffff !important;
          }
          .standard-document-preview td {
            padding: 8px 10px !important;
            border: 1px solid #000000 !important;
            color: #222222 !important;
            font-size: 0.95rem !important;
            line-height: 1.5 !important;
          }
          .standard-document-preview hr {
            border: 0 !important;
            border-top: 1.5px solid #15b3a4 !important;
            margin: 1.5rem 0 !important;
          }
          .standard-document-preview .signatures-container {
            display: flex !important;
            gap: 1.5rem !important;
            margin-top: 2rem !important;
            flex-wrap: wrap !important;
          }
          .standard-document-preview .signature-card {
            flex: 1 !important;
            min-width: 240px !important;
            border: 1px solid #cbd5e1 !important;
            padding: 1.5rem !important;
            background-color: #f8fafc !important;
          }
          .pptx-slide-container {
            container-type: inline-size !important;
          }
        ` }} />
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <h2 className="font-semibold text-lg text-foreground truncate max-w-[400px]" title={filename}>
              {filename}
            </h2>
            <span className="text-xs text-muted-foreground uppercase font-mono tracking-wider">
              {format} Preview
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Edit Mode Pill Switch */}
            {canEdit && ['docx', 'html', 'md', 'pdf', 'pptx'].includes(format) && (
              <div className="flex items-center gap-2 mr-2">
                <span className="text-xs font-semibold text-foreground select-none">Edit Mode</span>
                <button
                  type="button"
                  onClick={handleToggleEditMode}
                  className={cx(
                    "relative inline-flex h-7 w-16 shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors duration-200 ease-in-out focus:outline-none select-none",
                    isEditing ? "bg-[#10B981]" : "bg-[#9CA3AF]"
                  )}
                  title={isEditing ? "Turn Edit Mode OFF" : "Turn Edit Mode ON"}
                >
                  <span
                    className={cx(
                      "absolute text-[10px] font-bold tracking-wider text-white transition-opacity duration-200 select-none",
                      isEditing ? "left-2.5 opacity-100" : "right-2.5 opacity-100"
                    )}
                  >
                    {isEditing ? "ON" : "OFF"}
                  </span>
                  <span
                    className={cx(
                      "pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out",
                      isEditing ? "translate-x-9" : "translate-x-0"
                    )}
                  />
                </button>
              </div>
            )}

            {isEditing && format !== 'md' && (
              <div className="flex items-center gap-1.5 mr-2 select-none">
                <span className="text-xs font-semibold text-foreground">Font:</span>
                <select
                  value={selectedFont}
                  onChange={(e) => {
                    setSelectedFont(e.target.value);
                    setIsDirty(true);
                    setIsSessionDirtyState(true);
                  }}
                  className="bg-background border border-border rounded px-2.5 py-1 text-xs font-semibold text-foreground focus:ring-1 focus:ring-primary focus:outline-none"
                >
                  {['Inter', 'Montserrat', 'Helvetica', 'Calibri'].map((font) => (
                    <option key={font} value={font}>
                      {font}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Save Button */}
            {(isDirty || isSessionDirtyState) && (
              <Button
                onClick={handleSaveChangesToDb}
                disabled={saving}
                title="Save changes to database"
                className="gap-1.5 h-9 px-3.5 text-xs font-semibold rounded-md border border-emerald-500 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500 hover:text-white transition-all cursor-pointer"
              >
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5" />
                )}
                Save
              </Button>
            )}

            <Button
              onClick={handleDownload}
              title="Download file"
              disabled={saving}
              className="inline-flex items-center justify-center rounded-md border border-border hover:border-primary hover:bg-secondary-foreground/10 h-9 px-4 text-xs font-semibold bg-secondary text-secondary-foreground gap-1.5 transition-all cursor-pointer disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              {saving ? 'Generating...' : 'Download'}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              title="Close preview"
              className="h-9 w-9 p-0 animate-scale-in"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content body */}
        <div className={cx(
          "flex-1 overflow-y-auto p-6 bg-[#f3f4f6] flex flex-col",
          (loading || error) ? "items-center justify-center" : "items-stretch"
        )}>
          {loading && (
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <span className="text-sm font-medium">Loading preview...</span>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center gap-4 text-center max-w-md p-6 bg-destructive/5 border border-destructive/20 rounded-lg">
              <AlertCircle className="w-10 h-10 text-destructive animate-pulse" />
              <div>
                <h3 className="font-semibold text-destructive mb-1">Preview Generation Failed</h3>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
              <Button variant="subtle" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          )}

          {!loading && !error && (
            <div className={cx(
              "w-full h-full flex flex-col",
              (format === 'pptx') ? "items-center justify-start py-6" : "items-stretch justify-start"
            )}>
              {/* EDIT MODE OFF: Render read-only previews */}
              {!isEditing && (
                <>
                  {/* Markdown View */}
                  {format === 'md' && (
                    <div className="w-full flex flex-col items-center pb-12">
                      <div className="w-[816px] mx-auto bg-[#1e1e1e] border border-[#334155] shadow-2xl rounded-sm min-h-[1056px] flex flex-col relative my-4 overflow-hidden">
                        <div className="w-full h-1 bg-[#15b3a4]" />
                        <pre className="flex-1 p-8 font-mono text-sm text-slate-100 bg-[#1e1e1e] whitespace-pre-wrap overflow-auto leading-relaxed select-text outline-none">
                          {editedMarkdown}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Word (DOCX) HTML View */}
                  {format === 'docx' && (
                    <div className="w-full flex flex-col items-center pb-12">
                      <div className="w-[816px] mx-auto bg-white border border-[#cbd5e1] shadow-2xl rounded-sm min-h-[1056px] flex flex-col relative my-4 overflow-hidden">
                        <div className="w-full h-1 bg-[#15b3a4]" />
                        <div
                          className={cx(BRANDED_CLASSES, "branded-content flex-1")}
                          dangerouslySetInnerHTML={{ __html: editedHtml }}
                        />
                      </div>
                    </div>
                  )}

                  {/* PDF Native Viewer Iframe View */}
                  {format === 'pdf' && (
                    <iframe
                      key={pdfKey}
                      src={downloadUrl(`/standardize/preview/${fileId}?t=${pdfKey}`)}
                      className="w-full h-full border border-border rounded-lg bg-white min-h-[60vh] flex-1 shadow-md"
                      title="PDF Preview"
                    />
                  )}

                  {/* HTML View */}
                  {format === 'html' && (
                    (hasSessionEdits || isDirty) ? (
                      <div className="w-full flex flex-col items-center pb-12">
                        <div className="w-[816px] mx-auto bg-white border border-[#cbd5e1] shadow-2xl rounded-sm min-h-[1056px] flex flex-col relative my-4 overflow-hidden">
                          <div className="w-full h-1 bg-[#15b3a4]" />
                          <div
                            className={cx(BRANDED_CLASSES, "branded-content flex-1")}
                            dangerouslySetInnerHTML={{ __html: editedHtml }}
                          />
                        </div>
                      </div>
                    ) : (
                      <iframe
                        src={downloadUrl(`/standardize/preview/${fileId}`)}
                        className="w-full h-full border border-border rounded-lg bg-white min-h-[60vh] flex-1 shadow-md"
                        title="HTML Preview"
                      />
                    )
                  )}

                  {/* PowerPoint (PPTX) slide view */}
                  {format === 'pptx' && data?.type === 'pptx' && data.slides && (
                    <div className="w-full max-w-4xl flex flex-col gap-8 items-center justify-start py-6 pb-12">
                      {data.slides.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No slides found in this presentation.</p>
                      ) : (
                        data.slides.map((_: any, idx: number) => (
                          <div key={idx} className="w-full">
                            {renderSlide(idx, false)}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}

              {/* EDIT MODE ON: Render editor views */}
              {isEditing && (
                <>
                  {/* Rich Text Editor for DOCX, HTML, PDF */}
                  {['docx', 'html', 'pdf'].includes(format) && (
                    <div className="w-full flex flex-col items-center">
                      <div className="w-[816px] mb-3 px-4 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 text-xs font-semibold flex items-center justify-between animate-fade-in select-none">
                        <div className="flex items-center gap-2">
                          <Edit className="w-3.5 h-3.5" />
                          <span>Editing Mode Active — Click anywhere on the page to edit content directly</span>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider font-mono bg-emerald-500/20 px-2 py-0.5 rounded text-emerald-700">Live Sync</span>
                      </div>

                      <div className="w-[816px] mx-auto bg-white border border-[#cbd5e1] shadow-2xl rounded-sm min-h-[1056px] flex flex-col relative my-2 overflow-hidden transition-all duration-200 focus-within:ring-2 focus-within:ring-[#15b3a4]/40">
                        <div className="w-full h-1 bg-[#15b3a4]" />
                        <div
                          ref={editorRef}
                          contentEditable={true}
                          suppressContentEditableWarning
                          onInput={handleContentInput}
                          onKeyUp={handleContentInput}
                          onPaste={handleContentInput}
                          className={cx("flex-1 outline-none", BRANDED_CLASSES)}
                        />
                      </div>
                    </div>
                  )}

                  {/* Markdown Raw Text Editor */}
                  {format === 'md' && (
                    <div className="w-full flex flex-col items-center">
                      <div className="w-[816px] mb-3 px-4 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 text-xs font-semibold flex items-center justify-between animate-fade-in select-none">
                        <div className="flex items-center gap-2">
                          <Edit className="w-3.5 h-3.5" />
                          <span>Editing Mode Active — Edit raw Markdown directly</span>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider font-mono bg-emerald-500/20 px-2 py-0.5 rounded text-emerald-700">Markdown Editor</span>
                      </div>

                      <div className="w-[816px] mx-auto bg-[#1e1e1e] border border-[#334155] shadow-2xl rounded-sm min-h-[1056px] flex flex-col relative my-2 overflow-hidden transition-all duration-200 focus-within:ring-2 focus-within:ring-[#15b3a4]/40">
                        <div className="w-full h-1 bg-[#15b3a4]" />
                        <textarea
                          value={editedMarkdown}
                          onChange={(e) => {
                            setEditedMarkdown(e.target.value);
                            handleMarkDirty();
                          }}
                          className="flex-1 w-full p-8 font-mono text-sm text-slate-100 bg-[#1e1e1e] min-h-[1056px] resize-none outline-none border-none leading-relaxed"
                          placeholder="Write markdown here..."
                        />
                      </div>
                    </div>
                  )}

                  {/* Slide Canvas Editor for PPTX */}
                  {format === 'pptx' && data?.slides && (
                    <div className="w-full max-w-4xl flex flex-col gap-4 items-center justify-center pb-12">
                      <div className="w-full mb-1 px-4 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 text-xs font-semibold flex items-center justify-between animate-fade-in select-none">
                        <div className="flex items-center gap-2">
                          <Edit className="w-3.5 h-3.5" />
                          <span>Editing Mode Active — Click on any text box inside the slide to edit text directly</span>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider font-mono bg-emerald-500/20 px-2 py-0.5 rounded text-emerald-700">Slide Canvas</span>
                      </div>
                      {data.slides.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No slides found in this presentation.</p>
                      ) : (
                        data.slides.map((_: any, idx: number) => (
                          <div key={idx} className="w-full flex flex-col gap-2">
                            <div className="flex items-center justify-between w-full pl-1">
                              <span className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">Slide {idx + 1}</span>
                              {isEditing && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSlide(idx)}
                                  className="text-xs text-red-500 hover:text-red-700 font-semibold transition cursor-pointer"
                                >
                                  Delete Slide
                                </button>
                              )}
                            </div>
                            {renderSlide(idx, true)}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal Popups */}
      {confirmModal?.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-card border border-border rounded-lg shadow-2xl max-w-md w-full p-6 animate-scale-in flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-amber-500/10 text-amber-500 shrink-0 mt-0.5">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-base">{confirmModal.title}</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{confirmModal.message}</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-2 pt-3 border-t border-border">
              {confirmModal.type === 'download_unsaved' ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleConfirmAction('cancel')}
                    className="text-xs h-8 px-3"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={() => handleConfirmAction('download_without_save')}
                    className="text-xs h-8 px-3"
                  >
                    Download Without Saving
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleConfirmAction('confirm')}
                    className="text-xs h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Save & Download
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={() => handleConfirmAction('cancel')}
                    className="text-xs h-8 px-3"
                  >
                    No, Keep Editing
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleConfirmAction('confirm')}
                    className="text-xs h-8 px-3"
                  >
                    Yes, Discard Changes
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Feedback Notification Banner */}
      {toast && (
        <div className={cx(
          "fixed bottom-6 right-6 z-[70] flex items-center gap-2.5 px-4 py-3 rounded-lg shadow-xl text-xs font-semibold border animate-rise select-none",
          toast.type === 'success' ? "bg-emerald-900 text-emerald-100 border-emerald-700" : "bg-destructive text-destructive-foreground border-destructive"
        )}>
          {toast.type === 'success' ? (
            <Check className="w-4 h-4 text-emerald-400 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0" />
          )}
          <span>{toast.text}</span>
        </div>
      )}
    </div>,
    document.body
  );
}
